import {
  dataPost,
  dataSAMLError,
  dataSSOConfig,
  presenterFile,
  presenterHub,
  presenterOrganization,
  Post,
  presenterSession,
  presenterUser,
  presenterUserInvite,
  sparq3FilterPayload,
} from '@RestApi/types'
import fetch, { RequestInit, Response as nodeFetchResponse } from 'node-fetch'
import parseLinkHeader from 'parse-link-header'
import qs from 'querystring'
import { presenterNewsletter } from './types'
import { collection } from '@Modules/settings/collections/atoms'
export interface Request {
  method?: string
  path: string
  query?: any
  headers?: any
  data?: any
}

export interface Response {
  error?: any // Error / handled exception, ie. when client fails to fetch the data.
  status: number // HTTP response code.
  nextPageQuery?: any
  prevPageQuery?: any
  vctraceid: string
}

// Type returned from the request() helper. It includes the untyped JSON data received from the REST API.
export interface ResponseWithUntypedData extends Response {
  data?: any
}

export interface SessionResponse extends Response {
  data?: presenterSession
}
export interface InvitedUsersResponse extends Response {
  data: presenterUserInvite[]
}

export interface UsersResponse extends Response {
  data: presenterUser[]
}
export interface HubResponse extends Response {
  data?: presenterHub
}
export interface HubsResponse extends Response {
  data: presenterHub[]
}
export interface OrgResponse extends Response {
  data?: presenterOrganization
}
export interface OrgsResponse extends Response {
  data: presenterOrganization[]
}
export interface PostResponse extends Response {
  data?: Post
}
export interface PostsResponse extends Response {
  data: Post[]
}
export interface FileResponse extends Response {
  data?: presenterFile
}
export interface NewsletterResponse extends Response {
  data?: presenterNewsletter
}
export interface NewslettersResponse extends Response {
  data: presenterNewsletter[]
}
export interface NewsletterPreviewRequest {
  post: Post
}
export interface HTML {
  html: string
}
export interface NewsletterPreviewResponse extends Response {
  data?: HTML
}
export interface SSOConfigResponse extends Response {
  data: dataSSOConfig
}
export interface SSOConfigsResponse extends Response {
  data: dataSSOConfig[]
}
export interface SSOErrorDataResponse extends Response {
  data: dataSAMLError[]
}

// Hubs REST API client.
// Handles exceptions. All methods return well-defined type including status and error field.
export default class RestApiClient {
  apiUrl: string
  jwt: string
  csrfToken: string
  VERSION: string
  debug: boolean

  constructor(apiUrl: string, jwt: string, csrfToken: string, VERSION: string, debug: boolean) {
    this.apiUrl = apiUrl
    this.jwt = jwt
    this.csrfToken = csrfToken
    this.VERSION = VERSION
    this.debug = debug
  }

  ApiUrl = (): string => {
    return this.apiUrl
  }

  Ping = async (): Promise<Response> => {
    const resp = await this.request({
      path: '/ping',
    })
    return <Response>{
      error: resp.error,
      status: resp.status,
    }
  }

  GetSession = async (): Promise<SessionResponse> => {
    const resp = await this.request({
      path: '/session',
    })
    return <SessionResponse>{
      data: <presenterSession>resp.data,
      error: resp.error,
      status: resp.status,
    }
  }

  getProviderUsageCount = async (hubId: string, provider: string) => {
    const { data } = await this.request({
      method: 'GET',
      path: `/hubs/${hubId}/auth/${provider}/count`,
    })

    return data
  }

  setUserRole = async (hubUser: presenterUser, hubId: string): Promise<Response> => {
    const resp = await this.request({
      method: 'PUT',
      path: `/hubs/${hubId}/users/${hubUser.id}`,
      data: hubUser,
    })
    return <Response>resp
  }

  fetchHubSignInWorkflow = async (
    email: string,
    hubId: string,
    isResetPassword = false
  ): Promise<{ action: string } | undefined> => {
    try {
      const resp = await this.request({
        method: 'POST',
        path: `/hubs/${hubId}/users/lookup`,
        data: { email },
      })
      if (resp.data.redirect_url && resp.data.redirect_url !== '') {
        if (isResetPassword) {
          window.location.href = `${resp.data.redirect_url}&resetPasswordForm=true`
          return <{ action: string }>resp.data
        } else {
          window.location.href = resp.data.redirect_url
          return <{ action: string }>resp.data
        }
      } else {
        return <{ action: string }>resp.data
      }
    } catch (e) {
      // need to solve the issue with connecting stores together ( authStore <=> authcommands )
      // console.error(e)
      //show(this.LocaleStore.formatMessage(messages.authLoginFailed), DANGER)
    }
  }

  requestResetPassword = async (email: string, hub: presenterHub) => {
    const path = `/hubs/${hub.id}/password_reset_intent`
    const resp = await this.request({
      method: 'POST',
      path,
      data: { email },
    })
    return resp
  }

  signIn = async (
    email: string,
    password: string,
    redirect = '/',
    hubId: string,
    orgId: string
  ) => {
    // try {
    //getSafeStorage('local').clear()
    //getSafeStorage('session').clear()
    const resp = await this.request({
      method: 'POST',
      path: '/auth',
      data: { email, password, hub_id: hubId, org_id: orgId },
    })

    return resp
    /*  const { options } = this.AuthStore

      this.SessionStore.loadSession(resp.data)
      if (options && options.redirect) {
        redirect = options.redirect
      }
      runInAction(() => {
        this.AuthStore.options = null
      })

      reload(redirect)
      return true
    } catch (err) {
      console.error(err)
      return false
    }
    */
  }

  fetchCollections = async (hubId: string, includeArchived?: boolean) => {
    const resp = await this.request({
      path: `/hubs/${hubId}/collections`,
      query: { limit: 100, archived: includeArchived },
    })

    return resp
  }

  fetchCollection = async (hubId: string, collectionId: string) => {
    const resp = await this.request({
      path: `/hubs/${hubId}/collections/${collectionId}`,
    })
    return resp
  }

  updateCollection = async (hubId: string, collectionId: string, updatedCollection: collection) => {
    const resp = await this.request({
      method: 'PUT',
      path: `/hubs/${hubId}/collections/${collectionId}`,
      data: updatedCollection,
    })
    return resp
  }

  changeOrder = async (hubId: ID, collectionsList: any) => {
    const resp = await this.request({
      method: 'PUT',
      path: `/hubs/${hubId}/collections/reorder`,
      data: collectionsList,
    })
    return resp
  }

  UploadImage = async (hubId: string, file: Blob): Promise<FileResponse> => {
    const form = new FormData()
    form.append('attachment', file)
    const resp = await this.request({
      method: 'POST',
      path: `/hubs/${hubId}/files`,
      data: form,
    })
    return <FileResponse>{
      data: <presenterFile>resp.data,
      error: resp.error,
      status: resp.status,
    }
  }

  GetHubById = async (hubId: string): Promise<HubResponse> => {
    return await this.request({
      path: `/hubs/${hubId}`,
    })
  }

  GetPostDetail = async (hubId: string, postId: string) => {
    return await this.request({
      path: `/hubs/${hubId}/posts/${postId}`,
    })
  }

  // Rename to GetPosts. Consider adding type: string, so we can fetch published/drafts/scheduled/submissions etc.
  FetchAllPosts = async (hubId: string, query: any): Promise<PostsResponse> => {
    const resp = await this.request({
      path: `/hubs/${hubId}/posts`,
      query: query,
    })
    return <PostsResponse>{
      ...resp,
      data: <Post[]>(resp.data || []),
    }
  }

  CreateNewsletterDraft = async (hubId: string): Promise<NewsletterResponse> => {
    const resp = await this.request({
      method: 'POST',
      path: `/hubs/${hubId}/newsletters/v2`,
    })
    return <NewsletterResponse>{
      ...resp,
      data: <presenterNewsletter>resp.data,
    }
  }

  GetNewsletter = async (hubId: string, newsletterId: string): Promise<NewsletterResponse> => {
    const resp = await this.request({
      path: `/hubs/${hubId}/newsletters/${newsletterId}`,
    })
    return <NewsletterResponse>{
      ...resp,
      data: <presenterNewsletter>resp.data,
    }
  }

  // Note: Make sure to update Newsletter Posts before callling this method.
  // This method can be used to [Save] draft, but also to [Send Now] and [Schedule].
  UpdateNewsletter = async (
    hubId: string,
    newsletterId: string,
    newsletter: presenterNewsletter
  ): Promise<NewsletterResponse> => {
    const resp = await this.request({
      method: 'PUT',
      path: `/hubs/${hubId}/newsletters/${newsletterId}/v2`,
      data: newsletter,
    })
    return <NewsletterResponse>{
      ...resp,
      data: <presenterNewsletter>resp.data,
    }
  }

  GetNewsletterPost = async (hubId: string, postId: string): Promise<PostResponse> => {
    const resp = await this.request({
      path: `/hubs/${hubId}/posts/newsletters/${postId}`,
    })
    return <PostResponse>{
      ...resp,
      data: <Post>resp.data,
    }
  }

  UpdateNewsletterPost = async (
    hubId: string,
    postId: string,
    rootPost: Post
  ): Promise<PostResponse> => {
    const resp = await this.request({
      method: 'PUT',
      path: `/hubs/${hubId}/posts/newsletters/${postId}`,
      data: rootPost,
    })
    return <PostResponse>{
      ...resp,
      data: <Post>resp.data,
    }
  }

  updateHub = async (hubId: string, newData: presenterHub): Promise<HubResponse> => {
    const resp = await this.request({
      method: 'PUT',
      path: `/hubs/${hubId}`,
      data: newData,
    })
    return <HubResponse>{
      ...resp,
      data: <presenterHub>resp.data,
    }
  }

  NewsletterPreview = async (
    hubId: string,
    newsletterId: string,
    previewRequest: NewsletterPreviewRequest
  ): Promise<NewsletterPreviewResponse> => {
    const resp = await this.request({
      method: 'POST',
      path: `/hubs/${hubId}/newsletters/${newsletterId}/preview/v2`,
      data: previewRequest,
    })
    return <NewsletterPreviewResponse>{
      ...resp,
      data: <HTML>resp.data,
    }
  }

  NewsletterSendTest = async (
    hubId: string,
    newsletterId: string,
    rootPost: Post,
    subject: string,
    recepients: Array<string>
  ): Promise<NewsletterResponse> => {
    const resp = await this.request({
      method: 'POST',
      path: `/hubs/${hubId}/newsletters/${newsletterId}/test/v2`,
      data: { post: rootPost, subject: subject, recipients: recepients },
    })
    return <NewsletterResponse>{
      ...resp,
      data: <presenterNewsletter>resp.data,
    }
  }

  fetchMembers = async (hubId: string, params: any = {}): Promise<UsersResponse> => {
    const resp = await this.request({
      path: `/hubs/${hubId}/users`,
      query: params,
    })

    return <UsersResponse>{ ...resp, data: <presenterUser[]>(resp.data || []) }
  }

  fetchSparqActiveMembers = async (hubId: string) => {
    const resp = await this.request({ path: `/hubs/${hubId}/sparq3/filters/activemembers` })
    return <any>{
      ...resp,
      data: <any[]>(resp.data || []),
    }
  }

  fetchSparqActivities = async (hubId: string): Promise<sparq3FilterPayload> => {
    const resp = await this.request({
      path: `/hubs/${hubId}/sparq3/activities`,
      query: {
        limit: 50,
        q: 'activityType="Sparq1Survey" OR activityType="Survey" OR activityType = "Forum"',
        sort: 'lastUpdateDate:d',
      },
    })
    return <any>{
      ...resp,
      data: <sparq3FilterPayload[]>(resp.data || []),
    }
  }

  fetchNextActivity = async (hubId: string, query: any) => {
    const resp = await this.request({
      path: `/hubs/${hubId}/sparq3/activities`,
      query: query,
    })
    return <any>{
      ...resp,
      data: <any[]>(resp.data || []),
    }
  }

  fetchSparqActivityStatus = async (hubId: string, activityId: ID, status: string) => {
    const resp = await this.request({
      path: `/hubs/${hubId}/sparq3/activities/${activityId}`,
      query: {
        status,
      },
    })
    return <any>{
      ...resp,
      data: <any[]>(resp.data || []),
    }
  }

  GetOrgadminOrganizations = async (query: any): Promise<OrgsResponse> => {
    const resp = await this.request({
      path: `/orgadmin/organizations`,
      query: query,
    })
    return <OrgsResponse>{
      ...resp,
      data: <presenterOrganization[]>(resp.data || []),
    }
  }

  fetchMailingLists = async (hubId: ID) => {
    const resp = await this.request({
      path: `/hubs/${hubId}/newsletters/mailinglists/`,
      query: {
        scope: 'all',
      },
    })
    // console.log('mailing list', resp.data)
    return <any>{
      ...resp,
      data: <any[]>(resp.data || []),
    }
  }

  fetchIndividualMailingList = async (hubId: string, itemId: string) => {
    const resp = await this.request({
      path: `/hubs/${hubId}/newsletters/mailinglists/${itemId}`,
    })
    // console.log('individual mailing list', resp.data)
    return <any>{
      ...resp,
      data: <any[]>(resp.data || []),
    }
  }

  OrgadminDeleteOrganization = async (id: string): Promise<Response> => {
    return await this.request({
      path: `/orgadmin/organizations/${id}`,
      method: `DELETE`,
    })
  }

  GetMemberGroup = async (hubId: string, query: any): Promise<Response> => {
    const resp = await this.request({
      path: `/hubs/${hubId}/sparq3/filters`,
      query: query,
    })
    return <Response>{
      ...resp,
      data: <presenterHub[]>(resp.data || []),
    }
  }

  GetIndividualMemberGroup = async (hubId: string, memberId: string): Promise<Response> => {
    const resp = await this.request({
      path: `/hubs/${hubId}/sparq3/filters/${memberId}`,
    })

    return <Response>{
      ...resp,
      data: <presenterHub[]>(resp.data || []),
    }
  }

  GetOrgadminHubs = async (query: any): Promise<HubsResponse> => {
    const resp = await this.request({
      path: `/orgadmin/hubs`,
      query: query,
    })
    return <HubsResponse>{
      ...resp,
      data: <presenterHub[]>(resp.data || []),
    }
  }

  GetOrgadminUsers = async (query: any): Promise<UsersResponse> => {
    const resp = await this.request({
      path: `/orgadmin/users`,
      query: query,
    })
    return <UsersResponse>{
      ...resp,
      data: <presenterUser[]>(resp.data || []),
    }
  }

  OrgadminImpersonateUserPrintLinksRedirectUrl = (userId: string): string => {
    return `${this.apiUrl}/orgadmin/users/${userId}/impersonate/links`
  }

  GetHubSSOConfigs = async (hubId: string): Promise<SSOConfigsResponse> => {
    const resp = await this.request({
      path: `/hubs/${hubId}/saml`,
    })
    return <SSOConfigsResponse>{
      ...resp,
      data: <dataSSOConfig[]>(resp.data || []),
    }
  }

  RetrieveCcpaRecords = async (hubId: number | string, format: string = 'csv') => {
    const resp = await this.request({
      path: `/hubs/${hubId}/consent`,
      query: {
        format,
      },
    })

    return <any>{
      ...resp,
      data: <any[]>(resp.data || []),
    }
  }

  fetchInvites = async (hubId: string, q?: string): Promise<InvitedUsersResponse> => {
    const resp = await this.request({
      path: `/hubs/${hubId}/invites`,
      query: { q },
    })
    return <InvitedUsersResponse>{ ...resp, data: <presenterUserInvite[]>(resp.data || []) }
  }

  revokeInvite = async (hash: string): Promise<Response> => {
    const resp = await this.request({
      method: 'DELETE',
      path: `/invites/${hash}`,
    })

    return <Response>resp
  }

  inviteViaEmail = async (hubId: string, data: any): Promise<Response> => {
    const resp = await this.request({
      method: 'POST',
      path: `/hubs/${hubId}/invites`,
      data,
    })
    return <Response>resp
  }

  CreateHubSSOConfig = async (
    hubId: string,
    newData: dataSSOConfig
  ): Promise<SSOConfigResponse> => {
    const resp = await this.request({
      method: 'POST',
      path: `/hubs/${hubId}/saml`,
      data: newData,
    })
    return <SSOConfigResponse>{
      ...resp,
      data: <dataSSOConfig>resp.data,
    }
  }

  UpdateHubSSOConfig = async (
    hubId: string,
    ssoConfigId: string,
    newData: dataSSOConfig
  ): Promise<SSOConfigResponse> => {
    const resp = await this.request({
      method: 'PUT',
      path: `/hubs/${hubId}/saml/${ssoConfigId}`,
      data: newData,
    })
    return <SSOConfigResponse>{
      ...resp,
      data: <dataSSOConfig>resp.data,
    }
  }

  FetchErrorSSO = async (
    hubId: string,
    ssoConfigId: string,
    query: any
  ): Promise<SSOErrorDataResponse> => {
    const resp = await this.request({
      path: `/hubs/${hubId}/saml/${ssoConfigId}/errors`,
      query: query,
    })
    return <SSOErrorDataResponse>{ ...resp, data: <dataSAMLError[]>(resp.data || []) }
  }

  DeleteErrorSSO = async (
    hubId: string,
    ssoConfigId: string,
    ssoErrorId: string
  ): Promise<Response> => {
    const resp = await this.request({
      method: 'DELETE',
      path: `/hubs/${hubId}/saml/${ssoConfigId}/errors/${ssoErrorId}`,
    })
    return resp
  }

  DeleteHubSSOConfig = async (hubId: string, ssoConfigId: string): Promise<Response> => {
    const resp = await this.request({
      method: 'DELETE',
      path: `/hubs/${hubId}/saml/${ssoConfigId}`,
    })
    return resp
  }

  LeaveCommunity = async (hubId: string): Promise<Response> => {
    const resp = await this.request({
      method: 'DELETE',
      path: `/hubs/${hubId}/users/me`,
    })
    return resp
  }
  RemoveUser = async (hubId: string, userId: string): Promise<Response> => {
    const resp = await this.request({
      method: 'DELETE',
      path: `/hubs/${hubId}/users/${userId}`,
    })
    return resp
  }

  SignOut = async (): Promise<Response> => {
    const resp = await this.request({
      method: 'DELETE',
      path: `/session`,
    })
    return resp
  }

  DeleteCollection = async (hubId: string, collectionId: string): Promise<Response> => {
    const resp = await this.request({
      method: 'DELETE',
      path: `/hubs/${hubId}/collections/${collectionId}`,
    })
    return resp
  }

  AddCollection = async (hubId: string, data: collection): Promise<Response> => {
    const resp = await this.request({
      method: 'POST',
      path: `/hubs/${hubId}/collections`,
      data,
    })
    return resp
  }
  // Fetches data from REST API endpoint. Handles exceptions and always returns an object.
  //
  // 1. If the fetched data is a single JSON object, it returns the object right away, ie.:
  //    {status: 200, error: null, ...data}
  //
  // 2. If the fetched data is a JSON array, it returns it in "items" property and
  //    also sets "next" and "prev" with query params to the next & previous pages.
  //    {status: 200, error: null, items: [{id: 1}, {id: 2}], next: {page: 3, limit: 10}, prev: {page: 1, limit: 10}}
  //
  // 3. On fetch() exception (ie. non 2xx response status), it returns object with error property:
  //    {error: {message: "some error"}}
  request = async (request: Request): Promise<ResponseWithUntypedData> => {
    let reqUrl = `${this.apiUrl}${request.path}`
    if (request.query) {
      // the following code simplified the api
      // query string can be passed in like {q: undefined, b: '', c: null}
      // and it removes before pass it to query string
      let total = 0
      let deleted = 0
      const queryCopy = { ...request.query }
      for (const key in queryCopy) {
        total++
        if (queryCopy[key] === undefined || queryCopy[key] === '' || queryCopy[key] === null) {
          delete queryCopy[key]
          deleted++
        }
      }
      // if no more keys left inside query object
      // don't bother append ? to the url
      const left = total - deleted
      if (left > 0) {
        reqUrl += `?${qs.stringify(queryCopy)}`
      }
    }

    const headers = {
      Accept: `application/vnd.next.${this.VERSION}+json`,
      cookie: `jwt=${this.jwt};`,
      Authorization: `Bearer ${this.jwt}`,
      ['x-csrf-token']: this.csrfToken,
      ...request.headers,
    }

    let response: ResponseWithUntypedData = {
      status: 420, // Default response status (420 "Enhance Your Calm") just in case we can't send request at all.
      vctraceid: '',
    }

    let resp: nodeFetchResponse
    try {
      let reqInit: RequestInit = {
        headers,
        method: request.method?.toUpperCase() || 'GET',
      }
      if (request.data) {
        if (Object.prototype.toString.call(request.data) === '[object FormData]') {
          reqInit.body = request.data
        } else {
          reqInit.body = JSON.stringify(request.data)
        }
      }
      resp = await fetch(reqUrl, reqInit)
      response.status = resp.status
      response.vctraceid = resp.headers.get('x-vctraceid') || 'none'

      if (resp.headers.has('Link')) {
        let links = parseLinkHeader(resp.headers.get('Link') || '')
        if (links) {
          if (links.next) {
            // delete links.next.rel
            // delete links.next.url
            response.nextPageQuery = {
              ...links.next,
              rel: null,
              url: null,
            }
          }
          if (links.prev) {
            // delete links.prev.rel
            // delete links.prev.url
            response.prevPageQuery = {
              ...links.prev,
              rel: null,
              url: null,
            }
          }
        }
      }
    } catch (e) {
      response.error = e
      if (this.debug)
        console.log(`>>> RestApiClient failed to fetch ${reqUrl}: ERROR: ${e}`, response)
      return response
    }

    if (resp.status !== 204) {
      // Get data from the response body.
      try {
        const format = resp.headers.get('content-type')
        const isFormatCsv = format === 'text/csv'

        if (isFormatCsv) {
          response.data = await resp.text()
        } else {
          response.data = await resp.json()
        }
      } catch (e) {
        response.error = e
      }
    }
    if (response.data?.error) {
      response.error = response.data.error
      response.data = null
    } else if (response.status >= 400) {
      response.error = `HTTP ${response.status}`
    }

    if (this.debug) console.log(`>>> RestApiClient ${reqUrl}: HTTP ${response.status}`)

    return response
  }
}

// @ts-ignore
const _presenterPost: Post = {
  type: 'unknown',
  content_hints: [],
  source: {},
  author: {},
  title: '',
  description: '',
  body: '',
  context: '',
  tags: [],
  media: [],
  oembed: {},
  etc: {},
  hash: '',
  status: 'draft',
  children: [],
  like_count: 0,
}

export const emptyPresenterPost = (type: string): Post => {
  return { ..._presenterPost, type: type }
}

// @ts-ignore
const _dataPost: dataPost = {
  type: 'unknown',
  content_hints: [],
  source: {},
  author: {},
  title: '',
  description: '',
  body: '',
  context: '',
  tags: [],
  media: [],
  oembed: {},
  etc: {},
  hash: '',
  status: 'draft',
  children: [],
}

export const emptyDataPost = (type: string): dataPost => {
  switch (type) {
    case 'headingBlock':
      return { ..._dataPost, type: type, etc: { title_align: 'left', title_size: 'h1' } }
    case 'textBlock':
      return { ..._dataPost, type: type, etc: { body_align: 'left' } }
    case 'textImageBlock': // fall-through
    case 'imageTextBlock':
      return { ..._dataPost, type: type, etc: { body_align: 'left' } }
    default:
      return { ..._dataPost, type: type }
  }
}
